/**
 * Jumbo's deliveries: what you order comes on a dispatch rider, from the hub to where you're staying: your home in
 * your own country, or your hotel's front desk abroad (nowhere to stay here: there's no address to bring it to).
 * The rider leaves after packing; you can watch how far off they are (km, minutes), and when it lands you're told.
 * Until then it isn't yours to use: the laptop isn't on the table yet.
 */
import { type LifeEvent, type LifeState } from './life.ts';
import { hereCountry } from './character.ts';
import { abroad, homeCountry } from './travel.ts';
import { hustleOf } from './hustle.ts';
import { hotelFor, stayOf } from './hotel.ts';
import { Rng } from './rng.ts';
import { homesAbroad, marketOf } from './housing.ts';

/** key: what arrives (gadget:<id> into your setup, jewel:<id> / hat:<id> into what you own) */
export interface Order { /** the country it's delivered in */ country?: string; id: string; key: string; label: string; placed: number; leaves: number; eta: number; km: number; to: 'home' | 'hotel'; place: string }
const of = (s: LifeState): Order[] => ((s as LifeState & { orders?: Order[] }).orders ??= []);
export const ordersOf = of;
export const onItsWay = (s: LifeState, key: string) => of(s).some((o) => o.key === key && (!key.startsWith('gadget:') || (o.country ?? hereCountry(s)) === hereCountry(s)));

/** where Jumbo would bring it right now (null: nowhere to stay in this country) */
export function deliveryAddress(s: LifeState): { to: 'home' | 'hotel'; place: string } | null {
  if (hereCountry(s) === homeCountry(s)) return { to: 'home', place: s.card.home.area };
  // a home you own in this country (a player at their colonial house in the US: "why does Jumbo say it's delivering to
  // the hotel?"): it comes to your door there
  const own = homesAbroad(s).filter((h) => marketOf(h.region).country === hereCountry(s)).at(-1);
  if (own) return { to: 'home', place: own.area };
  const st = stayOf(s);
  if (st) return { to: 'hotel', place: st.hotel };
  // on a trip: where you're staying (the hotel in the city, the chambre d'hôte in the village)
  const away = abroad(s), c = hereCountry(s);
  if (away) return { to: 'hotel', place: away === 'village' ? 'your chambre d’hôte, Saint-Aurèle' : hotelFor(c === 'usa' ? 'us' : c) };
  return null;
}

/** an order: the rider leaves after packing (15–40 min), riding from a hub a few km away */
export function placeOrder(s: LifeState, key: string, label: string): Order | null {
  const addr = deliveryAddress(s);
  if (!addr) return null;
  const r = new Rng((s.seed ^ Math.imul(Math.floor(s.minutes) + key.length * 977 + of(s).length, 0x9e3779b1)) >>> 0);
  const lagos = hereCountry(s) === 'nigeria';
  const km = Math.round(r.range(lagos ? 6 : 3, lagos ? 21 : 12) * 10) / 10;
  const kmh = lagos ? r.range(14, 24) : r.range(20, 30); // (Lagos traffic: the bike weaves, but it's still Lagos)
  const leaves = s.minutes + Math.round(r.range(15, 40));
  const o: Order = { country: hereCountry(s), id: `o${s.minutes}-${of(s).length}`, key, label, placed: s.minutes, leaves, eta: leaves + Math.round((km / kmh) * 60), km, ...addr };
  of(s).push(o);
  return o;
}

/** where the rider is: packing, on the way (km and minutes left), or there */
export function orderProgress(s: LifeState, o: Order): { stage: 'packing' | 'riding' | 'here'; kmLeft: number; minsLeft: number; done: number } {
  if (s.minutes >= o.eta) return { stage: 'here', kmLeft: 0, minsLeft: 0, done: 1 };
  if (s.minutes < o.leaves) return { stage: 'packing', kmLeft: o.km, minsLeft: Math.ceil(o.eta - s.minutes), done: 0 };
  const k = (s.minutes - o.leaves) / Math.max(1, o.eta - o.leaves);
  return { stage: 'riding', kmLeft: Math.max(0.1, Math.round(o.km * (1 - k) * 10) / 10), minsLeft: Math.max(1, Math.ceil(o.eta - s.minutes)), done: k };
}

/** (every tick) what's arrived: yours now, and you're told where it is */
export function deliverOrders(s: LifeState, events: LifeEvent[]) {
  const list = of(s);
  if (!list.length) return;
  const due = list.filter((o) => o.eta <= s.minutes);
  if (!due.length) return;
  (s as LifeState & { orders?: Order[] }).orders = list.filter((o) => o.eta > s.minutes);
  const naija = hereCountry(s) === 'nigeria'; // (pidgin where it lands in Nigeria; plain English everywhere else)
  for (const o of due) {
    const [kind, id] = [o.key.slice(0, o.key.indexOf(':')), o.key.slice(o.key.indexOf(':') + 1)];
    if (kind === 'gadget') { const h = hustleOf(s), k = o.country && o.country !== homeCountry(s) ? `${id}@${o.country}` : id; if (!h.gadgets.includes(k)) h.gadgets.push(k); } // (a setup in the country it came to)
    else if (!s.owned.includes(o.key)) s.owned.push(o.key);
    const where = o.to === 'hotel' ? `The front desk at ${o.place} sent it up: it’s in your room.` : naija ? 'The rider brought it in: it’s in your place, set up.' : 'It’s in your place, set up.';
    events.push({ t: s.minutes, kind: 'social', text: naija ? `📦 E don land! Your ${o.label.toLowerCase()} from Jumbo. ${where}` : `📦 Your ${o.label.toLowerCase()} has arrived. ${where}` });
  }
}
